import { useAuth } from '../auth';
import type { BiomeKind } from '../generated/BiomeKind';
import type { ClaimMeta } from '../generated/ClaimMeta';
import type { ClaimOverlay } from '../generated/ClaimOverlay';
import type { CreateClaimResponse } from '../generated/CreateClaimResponse';
import type { MegaWorldId } from '../generated/MegaWorldId';
import type { ServerVisibility } from '../generated/ServerVisibility';

const PLATFORM_URL =
  (import.meta.env['VITE_PLATFORM_URL'] as string | undefined) ||
  'http://localhost:7001';

function getToken(): string {
  const token = useAuth.getState().accessToken;
  if (!token) throw new Error('Not authenticated');
  return token;
}

export interface Bounds {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface MegaWorldInfoJson {
  id: MegaWorldId;
  name: string;
  seed: number;
  width: number;
  height: number;
  generation_id: string;
}

export type SoilKind =
  | 'Alluvial'
  | 'Loam'
  | 'Clay'
  | 'Sandy'
  | 'Rocky'
  | 'Peat';

export type RockKind =
  | 'Granite'
  | 'Basalt'
  | 'Limestone'
  | 'Sandstone'
  | 'Slate'
  | 'Marble'
  | 'Schist'
  | 'Quartzite';

export interface MapTileDataJson {
  biome: BiomeKind;
  elevation: number;
  temperature: number;
  moisture: number;
  soil_type: SoilKind;
  rock_type: RockKind | null;
  mineral_density: number;
}

export interface RiverWirePointJson {
  x: number;
  y: number;
  accumulation: number;
}

export interface RiverSegmentJson {
  id: number;
  points: RiverWirePointJson[];
}

export interface LakeOverlayJson {
  id: number;
  surface_elevation: number;
  cells: ChunkCoordJson[];
}

export interface ChunkCoordJson {
  x: number;
  y: number;
}

export interface ClaimMetaJson {
  claim_id: number;
  owner_id: string;
  mega_world: MegaWorldId;
  name: string;
  origin: ChunkCoordJson;
  block_count: number;
  status: ClaimMeta['status'];
  visibility: ClaimMeta['visibility'];
  created_at: string;
}

export interface ClaimOverlayJson {
  claim_id: number;
  owner_name: string;
  name: string;
  origin: ChunkCoordJson;
  block_count: number;
  status: ClaimOverlay['status'];
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${PLATFORM_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    const refreshed = await useAuth.getState().refresh();
    if (!refreshed) {
      useAuth.getState().logout();
      throw new Error('401: session expired');
    }
    const retry = await fetch(`${PLATFORM_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
        ...init?.headers,
      },
    });
    if (!retry.ok) {
      const retryText = await retry.text();
      throw new Error(`${retry.status}: ${retryText}`);
    }
    const retryText = await retry.text();
    if (!retryText) return undefined as T;
    return JSON.parse(retryText) as T;
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export async function getMegaWorlds(): Promise<MegaWorldInfoJson[]> {
  return apiFetch('/api/megaworlds');
}

export async function getMyClaims(): Promise<ClaimMetaJson[]> {
  return apiFetch('/api/my-claims');
}

export async function getMapTiles(
  megaWorld: string,
  bounds: Bounds,
): Promise<MapTileDataJson[][]> {
  const { x1, y1, x2, y2 } = bounds;
  return apiFetch(
    `/api/map/${megaWorld}?x1=${x1}&y1=${y1}&x2=${x2}&y2=${y2}`,
  );
}

export async function getClaims(
  megaWorld: string,
  bounds: Bounds,
): Promise<ClaimOverlayJson[]> {
  const { x1, y1, x2, y2 } = bounds;
  return apiFetch(
    `/api/claims/${megaWorld}?x1=${x1}&y1=${y1}&x2=${x2}&y2=${y2}`,
  );
}

export async function createClaim(req: {
  mega_world: MegaWorldId;
  name: string;
  origin: ChunkCoordJson;
  visibility: ServerVisibility;
}): Promise<CreateClaimResponse> {
  return apiFetch('/api/claims', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function getClaim(claimId: number): Promise<ClaimMetaJson> {
  return apiFetch(`/api/claims/${claimId}`);
}

export async function getClaimChunks(claimId: number): Promise<ChunkCoordJson[]> {
  return apiFetch(`/api/claims/${claimId}/chunks`);
}

export interface ContiguityCheckJson {
  passed: boolean;
  disconnected_chunks: ChunkCoordJson[];
}

export interface MinWidthCheckJson {
  passed: boolean;
  narrow_chunks: ChunkCoordJson[];
}

export interface IslandCheckJson {
  passed: boolean;
  enclosed_chunks: ChunkCoordJson[];
  auto_fill_cost: number;
}

export interface ValidationResultJson {
  valid: boolean;
  contiguity: ContiguityCheckJson;
  min_width: MinWidthCheckJson;
  islands: IslandCheckJson;
}

export interface ExpandClaimResponseJson {
  claim: ClaimMetaJson;
  added_chunks: ChunkCoordJson[];
  validation: ValidationResultJson;
}

export interface ShrinkClaimResponseJson {
  claim: ClaimMetaJson;
  removed_chunks: ChunkCoordJson[];
}

export interface ValidationErrorResponseJson {
  validation: ValidationResultJson;
}

export interface ClaimTilesOverlayJson {
  claim_id: number;
  owner_name: string;
  is_own: boolean;
  tiles: ChunkCoordJson[];
}

export async function getClaimTiles(
  megaWorld: string,
  bounds: Bounds,
): Promise<ClaimTilesOverlayJson[]> {
  const { x1, y1, x2, y2 } = bounds;
  return apiFetch(
    `/api/claimtiles/${megaWorld}?x1=${x1}&y1=${y1}&x2=${x2}&y2=${y2}`,
  );
}

export async function expandClaim(
  claimId: number,
  chunks: ChunkCoordJson[],
  autoFill = false,
): Promise<ExpandClaimResponseJson> {
  return apiFetch(`/api/claims/${claimId}/expand`, {
    method: 'POST',
    body: JSON.stringify({ chunks, auto_fill: autoFill }),
  });
}

export async function shrinkClaim(
  claimId: number,
  chunks: ChunkCoordJson[],
): Promise<ShrinkClaimResponseJson> {
  return apiFetch(`/api/claims/${claimId}/shrink`, {
    method: 'POST',
    body: JSON.stringify({ chunks }),
  });
}

export async function getRivers(
  megaWorld: string,
  bounds: Bounds,
): Promise<RiverSegmentJson[]> {
  const { x1, y1, x2, y2 } = bounds;
  return apiFetch(
    `/api/map/${megaWorld}/rivers?x1=${x1}&y1=${y1}&x2=${x2}&y2=${y2}`,
  );
}

export async function getLakes(
  megaWorld: string,
  bounds: Bounds,
): Promise<LakeOverlayJson[]> {
  const { x1, y1, x2, y2 } = bounds;
  return apiFetch(
    `/api/map/${megaWorld}/lakes?x1=${x1}&y1=${y1}&x2=${x2}&y2=${y2}`,
  );
}

// ─── Friend API ──────────────────────────────────────────────────────

export interface FriendInfoJson {
  account_id: number;
  display_name: string;
  discriminator: number;
  since: string;
  online: boolean;
  world_id: number | null;
}

export interface AccountSearchResultJson {
  account_id: number;
  display_name: string;
  discriminator: number;
}

export interface FriendRequestJson {
  from_id: number;
  to_id: number;
  display_name: string;
  discriminator: number;
  created_at: string;
}

export interface FriendRequestsJson {
  incoming: FriendRequestJson[];
  outgoing: FriendRequestJson[];
}

export async function getFriends(): Promise<FriendInfoJson[]> {
  return apiFetch('/api/friends');
}

export async function searchUsers(query: string): Promise<AccountSearchResultJson[]> {
  return apiFetch(`/api/friends/search?q=${encodeURIComponent(query)}`);
}

export async function sendFriendRequest(toId: number): Promise<void> {
  await apiFetch('/api/friends/request', {
    method: 'POST',
    body: JSON.stringify({ to_id: toId }),
  });
}

export async function acceptFriendRequest(fromId: number): Promise<void> {
  await apiFetch(`/api/friends/${fromId}/accept`, { method: 'POST' });
}

export async function rejectFriendRequest(fromId: number): Promise<void> {
  await apiFetch(`/api/friends/${fromId}/reject`, { method: 'POST' });
}

export async function removeFriend(friendId: number): Promise<void> {
  await apiFetch(`/api/friends/${friendId}`, { method: 'DELETE' });
}

export async function blockUser(blockedId: number): Promise<void> {
  await apiFetch('/api/friends/block', {
    method: 'POST',
    body: JSON.stringify({ blocked_id: blockedId }),
  });
}

export async function getFriendRequests(): Promise<FriendRequestsJson> {
  return apiFetch('/api/friends/requests');
}

// ─── World API ───────────────────────────────────────────────────────

export interface WorldInfoJson {
  world_id: number;
  owner_id: number;
  claim_id: number | null;
  name: string;
  status: string;
  visibility: string;
  player_count: number;
  max_players: number;
  instance_id: string | null;
  created_at: string;
}

export async function createWorld(
  claimId: number,
  name: string,
  visibility = 'Public',
  maxPlayers = 20,
): Promise<WorldInfoJson> {
  return apiFetch('/api/worlds', {
    method: 'POST',
    body: JSON.stringify({
      claim_id: claimId,
      name,
      visibility,
      max_players: maxPlayers,
    }),
  });
}

export async function listWorlds(
  filter: 'owned' | 'joined' | 'public' = 'owned',
): Promise<WorldInfoJson[]> {
  return apiFetch(`/api/worlds?filter=${filter}`);
}

export async function getWorld(worldId: number): Promise<WorldInfoJson> {
  return apiFetch(`/api/worlds/${worldId}`);
}

export async function joinWorld(worldId: number): Promise<void> {
  await apiFetch(`/api/worlds/${worldId}/join`, { method: 'POST' });
}
